import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";

export const BASE_URL = "https://esoshotelquelle.com";
const OG_IMAGE = "https://storage.googleapis.com/gpt-engineer-file-uploads/LWmdENiduXdTolLj05Xw2KcFswk2/social-images/social-1774369533197-WhatsApp_Image_2026-03-24_at_15.26.52.webp";

const langMap: Record<string, string> = {
  de: "de",
  en: "en",
  fr: "fr",
  es: "es",
  it: "it",
};

export interface BreadcrumbItem {
  name: string;
  path: string;
}

interface SEOProps {
  title: string;
  description: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  breadcrumbs?: BreadcrumbItem[];
}

const hotelJsonLd = {
  "@context": "https://schema.org",
  "@type": "Hotel",
  name: "ESOS Hotel Quelle",
  url: BASE_URL,
  image: OG_IMAGE,
  description: "Boutique-Hotel mit komfortablen Zimmern und Apartments in Bad Ragaz, Schweiz.",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Badstrasse 9a",
    addressLocality: "Bad Ragaz",
    postalCode: "7310",
    addressRegion: "St. Gallen",
    addressCountry: "CH",
  },
  telephone: "+41 81 302 11 13",
  email: "reservation@esoshotelquelle.com",
  starRating: { "@type": "Rating", ratingValue: "3" },
  priceRange: "$$",
  checkinTime: "16:00",
  checkoutTime: "11:00",
};

function buildBreadcrumbJsonLd(breadcrumbs: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${BASE_URL}${item.path === "/" ? "" : item.path}`,
    })),
  };
}

const SEO = ({ title, description, jsonLd, breadcrumbs }: SEOProps) => {
  const { pathname } = useLocation();
  const { language } = useLanguage();
  const canonical = `${BASE_URL}${pathname === "/" ? "" : pathname}`;
  const fullTitle = pathname === "/" ? title : `${title} | ESOS Hotel Quelle`;

  const languages = ["de", "en", "fr", "es", "it"];

  // Collect all JSON-LD blocks
  const jsonLdBlocks: Record<string, unknown>[] = [];

  if (jsonLd) {
    if (Array.isArray(jsonLd)) {
      jsonLdBlocks.push(...jsonLd);
    } else {
      jsonLdBlocks.push(jsonLd);
    }
  } else {
    jsonLdBlocks.push(hotelJsonLd);
  }

  if (breadcrumbs && breadcrumbs.length > 0) {
    jsonLdBlocks.push(buildBreadcrumbJsonLd(breadcrumbs));
  }

  return (
    <Helmet>
      <html lang={langMap[language] || "de"} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {languages.map((lang) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={canonical} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={OG_IMAGE} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={OG_IMAGE} />

      {jsonLdBlocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
